import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ReactTable from "react-table";
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { firestore } from "../../../../helpers/Firebase";
import { accessTypes, createNotification } from "../../../../helpers/Utils";
import ChatLabels from "./ChatLabels";

const LabelsManagement = ({ onDismiss }) => {
    const { user } = useSelector(state => state.authUser);
    const [labels, setLabels] = useState([]);
    const [selectedLabel, setSelectedLabel] = useState(false);
    const [deleteLabel, setDeleteLabel] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!accessTypes.includes(user?.accessType)) {
            return;
        }

        const unsubscribe = firestore()
            .collection('chatLabels')
            .where('active', '==', true)
            .orderBy('created', 'desc')
            .onSnapshot(snap => {
                let data = snap?.docs?.map(doc => ({
                    ...doc.data(),
                    id: doc.id,
                    key: doc.id
                }));
                setLabels(data);
            });

        return () => unsubscribe();
    }, [user]);

    const deleteLabelFunc = async (label) => {
        setLoading(true);
        try {
            await firestore().collection('chatLabels').doc(label.id).update({
                active: false,
                updated: Date.now()
            });
            setDeleteLabel(false);
            createNotification('success', 'Label deleted successfully', 'Success');
        } catch (error) {
            console.error("Error deleting label:", error);
            createNotification('error', 'Failed to delete label', 'Error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (label) => {
        setDeleteLabel(label);
    };

    const handleEdit = (label) => {
        setSelectedLabel(label || {});
    };

    const handleCreate = () => {
        setSelectedLabel({ addNew: true });
    };

    const tableColumns = useMemo(() => [
        {
            Header: "Name",
            accessor: "name",
            Cell: props => (

                <div style={{ fontWeight: 500, color: '#2c3e50' }}>
                    {props.value}
                </div>
            ),
            sortable: false
        },
        {
            Header: "Color",
            accessor: "color",
            Cell: props => (
                <div
                    style={{
                        width: '60px',
                        height: '32px',
                        borderRadius: '6px',
                        backgroundColor: props.value || '#6c757d',
                        border: '1px solid #ddd',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                />

            ),
            sortable: false
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: props => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

                    <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => handleDelete(props.original)}
                        style={{
                            borderRadius: '6px',
                            fontWeight: 500,
                            padding: '4px 12px',
                            borderWidth: '1.5px'
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={() => handleEdit(props.original)}
                        style={{
                            borderRadius: '6px',
                            fontWeight: 500,
                            padding: '4px 12px',
                            borderWidth: '1.5px'
                        }}
                    >
                        Edit
                    </Button>
                </div>
            ),
            sortable: false,
            width: 150
        }
    ], []);

    if (!accessTypes.includes(user?.accessType)) {
        return null;
    }

    return (
        <>
            {loading && <div className="loading" />}

            {selectedLabel && (
                <ChatLabels
                    dismiss={() => setSelectedLabel(false)}
                    selectedLabel={selectedLabel}
                />
            )}

            <Modal backdrop="static" isOpen={!!deleteLabel}>
                <ModalHeader>Delete Label</ModalHeader>
                <ModalBody>
                    <Label className="mb-2">Are you sure you want to delete this label?</Label>
                    <p style={{ color: '#6c757d', fontSize: '14px' }}>
                        This will remove the label from all chats. The label will be marked as inactive.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={() => setDeleteLabel(false)}>Cancel</Button>
                    <Button color='danger' onClick={() => deleteLabelFunc(deleteLabel)}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                backdrop="static"
                isOpen={true}
                size="lg"
                style={{ zIndex: 1050 }}
            >

                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
                    borderBottom: '1px solid #e9ecef',
                    padding: '1.25rem 1.5rem',
                    backgroundColor: '#f8f9fa',
                    borderTopLeftRadius: '14px',
                    borderTopRightRadius: '14px'
                }}>
                    <h5 style={{ margin: 0, fontWeight: 600, color: '#2c3e50', flex: 1 }}>
                        Chat Labels Management
                    </h5>
                    <Button
                        color="primary"
                        onClick={handleCreate}
                        style={{
                            borderRadius: '6px',
                            fontWeight: 500,
                            padding: '6px 16px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        Create New
                    </Button>
                </div>
                <ModalBody style={{ padding: '1.5rem', paddingTop: '0px' }}>
                    {/* Table */}
                    {labels.length > 0 ? (
                        <ReactTable
                            data={labels}
                            columns={tableColumns}
                            minRows={0}
                            showPageJump={false}
                            showPageSizeOptions={false}
                            showPagination={false}
                            className='custuomReactTable'
                        />
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '3rem 1rem',
                            color: '#6c757d'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🏷️</div>
                            <h6 style={{ color: '#495057', marginBottom: '0.5rem' }}>
                                No labels yet
                            </h6>
                            <p style={{ fontSize: '14px', marginBottom: '1.5rem' }}>
                                Create your first label to get started
                            </p>
                            <Button
                                color="primary"
                                onClick={handleCreate}
                                style={{
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    padding: '8px 20px'
                                }}
                            >
                                Create Label
                            </Button>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter style={{
                    borderTop: '1px solid #e9ecef',
                    padding: '1rem 1.5rem',
                    backgroundColor: '#f8f9fa'
                }}>
                    <Button
                        color="secondary"
                        outline
                        onClick={onDismiss}
                        style={{
                            borderRadius: '6px',
                            fontWeight: 500,
                            padding: '6px 16px'
                        }}
                    >
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default LabelsManagement;
